import { Avatar, Box, Paper, Stack, Typography } from "@mui/material";
import InfoRow from "./InfoRow";
import type { BookingData } from "../../../../types/view-booking";

type DeceasedInfoCardProps = {
  deceased?: BookingData["beloved"];
};

const parseDDMMYYYY = (value?: string) => {
  if (!value) return null;

  const [dd, mm, yyyy] = value.split("-").map((x) => Number(x));
  if (!dd || !mm || !yyyy) return null;

  const d = new Date(yyyy, mm - 1, dd);

  if (
    d.getFullYear() !== yyyy ||
    d.getMonth() !== mm - 1 ||
    d.getDate() !== dd
  ) {
    return null;
  }

  return d;
};

const formatPrettyDateDDMMYYYY = (value?: string) => {
  const d = parseDDMMYYYY(value);
  if (!d) return "N/A";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(d);
};

const calculateAgeAtDeathDDMMYYYY = (dob?: string, dod?: string) => {
  const birth = parseDDMMYYYY(dob);
  const death = parseDDMMYYYY(dod);
  if (!birth || !death) return null;

  let age = death.getFullYear() - birth.getFullYear();
  const m = death.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && death.getDate() < birth.getDate())) age -= 1;

  return age >= 0 ? age : null;
};

export default function DeceasedInfoCard({ deceased }: DeceasedInfoCardProps) {
  const details = deceased?.details ?? {};

  console.log(details);

  const dob = details.dateOfBirth;
  const dod = details.dateOfDeath;

  const sunrise = formatPrettyDateDDMMYYYY(dob);
  const sunset = formatPrettyDateDDMMYYYY(dod);
  const age = calculateAgeAtDeathDDMMYYYY(dob, dod);

  return (
    <Paper
      sx={{ flex: 1, borderRadius: "12px", p: 2, boxShadow: 0, height: "100%" }}
    >
      <Typography
        sx={{ fontWeight: 600, fontSize: 16, color: "#64748b", mb: 2 }}
      >
        Deceased Information
      </Typography>

      <Box
        sx={{
          bgcolor: "#f0f4f8",
          borderRadius: "12px",
          p: 2,
          mb: 2,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar
            src={deceased?.avatar || undefined}
            alt={deceased?.name || "Deceased"}
            sx={{
              width: 72,
              height: 72,
              borderRadius: "16px",
              bgcolor: "#cbd5e1",
              fontWeight: 800,
            }}
          >
            {(deceased?.name || "N/A")
              .split(" ")
              .filter(Boolean)
              .slice(0, 2)
              .map((w) => w[0]?.toUpperCase())
              .join("")}
          </Avatar>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 900,
                fontSize: 18,
                color: "#0f172a",
                lineHeight: 1.1,
                textTransform: "uppercase",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={deceased?.name || ""}
            >
              {deceased?.name || "N/A"}
            </Typography>

            <Typography sx={{ fontSize: 12, color: "#64748b", mt: 0.5 }}>
              {age !== null ? `Age: ${age}` : "Age: N/A"}
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#64748b", mt: 0.5 }}>
              {deceased?.details.gender || "N/A"}
            </Typography>
          </Box>
        </Stack>

        {/* Sunrise / Sunset row */}
        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
          <Box
            sx={{
              flex: 1,
              bgcolor: "white",
              borderRadius: "10px",
              p: 1.5,
              border: "1px solid #e2e8f0",
            }}
          >
            <Typography
              sx={{ fontSize: 10, color: "#64748b", fontWeight: 700 }}
            >
              SUNRISE
            </Typography>
            <Typography
              sx={{ fontSize: 13, fontWeight: 800, color: "#0f172a" }}
            >
              {sunrise}
            </Typography>
          </Box>

          <Box
            sx={{
              flex: 1,
              bgcolor: "white",
              borderRadius: "10px",
              p: 1.5,
              border: "1px solid #e2e8f0",
            }}
          >
            <Typography
              sx={{ fontSize: 10, color: "#64748b", fontWeight: 700 }}
            >
              SUNSET
            </Typography>
            <Typography
              sx={{ fontSize: 13, fontWeight: 800, color: "#0f172a" }}
            >
              {sunset}
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Extra info rows (keep yours) */}
      <Stack spacing={0.5}>
        <InfoRow label="Nickname:" value={details.nickname || "N/A"} />
        <InfoRow label="Religion:" value={details.religion || "N/A"} />
        <InfoRow label="Denomination:" value={details.denomination || "N/A"} />
      </Stack>
    </Paper>
  );
}
